import { type Country, DateObject, useIntl } from '@capsicum/common';
import { GenericDrawer, TextInput } from '@capsicum/common-web';
import { type SubmitData } from '@capsicum/features/fanshop/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { object, string, type z } from 'zod';

export type DeliveryAddressProps = {
	city?: string;
	closeModal: () => void;
	country: Country;
	firstName?: string;
	lastName?: string;
	mobileNumber?: string;
	onClick: (data: SubmitData) => void;
	region?: string;
	street?: string;
	testID: string;
	zipCode?: string;
};

export const DeliveryAddress = ({
	city = '',
	closeModal,
	country,
	firstName = '',
	lastName = '',
	mobileNumber = '',
	onClick,
	region = '',
	street = '',
	testID,
	zipCode = ''
}: DeliveryAddressProps) => {
	const { $t } = useIntl();

	const countryName = country?.name || '';

	const schema = object({
		city: string().nonempty(
			$t('rewards.auctions.bids.addressDrawer.error.required', {
				fieldName: $t('rewards.auctions.bids.addressDrawer.city')
			})
		),
		country: string().nonempty(
			$t('rewards.auctions.bids.addressDrawer.error.required', {
				fieldName: $t('rewards.auctions.bids.addressDrawer.country')
			})
		),
		firstName: string().nonempty(
			$t('rewards.auctions.bids.addressDrawer.error.required', {
				fieldName: $t('rewards.auctions.bids.addressDrawer.firstName')
			})
		),
		lastName: string().nonempty(
			$t('rewards.auctions.bids.addressDrawer.error.required', {
				fieldName: $t('rewards.auctions.bids.addressDrawer.lastName')
			})
		),
		mobileNumber: string()
			.min(6, $t('auth.mobileNumberTooShort'))
			.regex(/^\+(?:[1-9]\d{0,3}|\([1-9]\d{0,3}\))(?:[.\-\s]?\d{1,}){1,14}$/, $t('auth.invalidMobileNumber')),
		region: string(),
		street: string().nonempty(
			$t('rewards.auctions.bids.addressDrawer.error.required', {
				fieldName: $t('rewards.auctions.bids.addressDrawer.street')
			})
		),
		zipCode: string().nonempty(
			$t('rewards.auctions.bids.addressDrawer.error.required', {
				fieldName: $t('rewards.auctions.bids.addressDrawer.zipCode')
			})
		)
	});

	type FormShape = z.infer<typeof schema>;

	const form = useForm<FormShape>({
		defaultValues: {
			city,
			country: countryName,
			firstName,
			lastName,
			mobileNumber,
			region,
			street,
			zipCode
		},
		mode: 'onChange',
		resolver: zodResolver(schema)
	});

	const prepareFormData = (data: FormShape): SubmitData => {
		return {
			...data,
			addressLabel: DateObject.now().toString(),
			countryCode: country.iso2
		};
	};

	const onSubmit = (data: FormShape) => {
		onClick(prepareFormData(data));
		closeModal();
	};

	return (
		<GenericDrawer
			buttonDisabled={!form.formState.isValid}
			buttonText={$t('rewards.auctions.bids.addressDrawer.button')}
			isButtonFullWidth
			testID={testID}
			title={$t('rewards.auctions.bids.addressDrawer.title')}
			onButtonPress={form.handleSubmit(onSubmit)}
		>
			<FormProvider {...form}>
				<S.StyledFormInput
					disabled
					label={$t('rewards.auctions.bids.addressDrawer.country')}
					name="country"
					testID="delivery-address-country"
				/>
				<S.StyledFormInput
					label={$t('rewards.auctions.bids.addressDrawer.firstName')}
					name="firstName"
					testID="delivery-address-firstName"
				/>

				<S.StyledFormInput
					label={$t('rewards.auctions.bids.addressDrawer.lastName')}
					name="lastName"
					testID="delivery-address-lastName"
				/>

				<S.StyledFormInput
					label={$t('rewards.auctions.bids.addressDrawer.phone')}
					name="mobileNumber"
					testID="delivery-address-mobileNumber"
					type="mobileNumber"
				/>

				<S.StyledFormInput
					label={$t('rewards.auctions.bids.addressDrawer.city')}
					name="city"
					testID="delivery-address-city"
				/>

				<S.StyledFormInput
					label={$t('rewards.auctions.bids.addressDrawer.street')}
					name="street"
					testID="delivery-address-street"
				/>

				<S.StyledFormInput
					label={$t('rewards.auctions.bids.addressDrawer.zipCode')}
					name="zipCode"
					testID="delivery-address-zipCode"
				/>

				<S.StyledFormInput
					label={$t('rewards.auctions.bids.addressDrawer.region')}
					name="region"
					testID="delivery-address-region"
				/>
			</FormProvider>
		</GenericDrawer>
	);
};

namespace S {
	export const StyledFormInput = styled(TextInput)`
		margin: ${({ theme }) => theme.spacings.xxsmall} 0;
	`;
}
